// src/pages/Auth/RegisterForm.jsx
// -----------------------------------------------------------------------------
// Registration screen for Auth registration. Matches the split-panel design,
// uses plain CSS variables from the theme, and validates client-side.
// Calls authService.register -> POST /api/Auth/register.
// -----------------------------------------------------------------------------
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../api/authService';
import { EMAIL_REGEX } from '../../utils/validationSchema';
import afixLogo from '../../assets/images/afix-logo.png';
import './auth.css';

export default function RegisterForm() {
  const navigate = useNavigate();

  // Form State matching the register payload schema
  const [formData, setFormData] = useState({
    first_Name: '',
    last_Name: '',
    phone_1: '',
    phone_2: '',
    email: '',
    address_Line_1: '',
    address_Line_2: '',
    address_Line_3: '',
    city: '',
    district: '',
    country: '',
    status: 'Active',
    role: 'Team Member',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.first_Name.trim()) newErrors.first_Name = 'First name is required';
    if (!formData.last_Name.trim()) newErrors.last_Name = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.status.trim()) newErrors.status = 'Status is required';
    if (!formData.role.trim()) newErrors.role = 'Role is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      toast.error('Please fix form validation errors.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        id: 0,
        ...formData,
      };

      await authService.register(payload);
      toast.success('Registration completed successfully!');
      navigate('/login');
    } catch (err) {
      const respData = err?.response?.data;
      if (respData?.errors && typeof respData.errors === 'object') {
        const fieldErrors = {};
        Object.keys(respData.errors).forEach((key) => {
          fieldErrors[key] = Array.isArray(respData.errors[key])
            ? respData.errors[key].join(' ')
            : String(respData.errors[key]);
        });
        setErrors(fieldErrors);
      }
      setError(respData?.message || err.message || 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen">
      {/* ---------- Left: Brand Panel ---------- */}
      <div className="auth-brand-panel">
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />

        <div className="auth-brand-content">
          <img src={afixLogo} alt="AFix logo" className="auth-brand-logo" />
          <h1>Afix</h1>
          <p className="auth-tagline">Track &middot; Fix &middot; Deliver</p>
          <p className="auth-sub">
            Create your account to start managing tickets, assignments and releases.
          </p>
        </div>
      </div>

      {/* ---------- Right: Registration Form Panel ---------- */}
      <div className="auth-form-panel" style={{ overflowY: 'auto', padding: '40px 20px' }}>
        <div className="auth-form-box" style={{ maxWidth: '640px', width: '100%' }}>
          <img src={afixLogo} alt="AFix" className="auth-form-logo" />
          <h2>Create Account</h2>
          <p className="subtitle">Register to begin tracking bugs and features</p>

          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label htmlFor="first_Name">First Name *</label>
                <input
                  id="first_Name"
                  name="first_Name"
                  value={formData.first_Name}
                  onChange={handleChange}
                  placeholder="e.g. John"
                  className={errors.first_Name ? 'input-error' : ''}
                />
                {errors.first_Name && <span className="error-text" style={{ color: 'var(--color-danger)', fontSize: '11px' }}>{errors.first_Name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="last_Name">Last Name *</label>
                <input
                  id="last_Name"
                  name="last_Name"
                  value={formData.last_Name}
                  onChange={handleChange}
                  placeholder="e.g. Doe"
                  className={errors.last_Name ? 'input-error' : ''}
                />
                {errors.last_Name && <span className="error-text" style={{ color: 'var(--color-danger)', fontSize: '11px' }}>{errors.last_Name}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
                className={errors.email ? 'input-error' : ''}
              />
              {errors.email && <span className="error-text" style={{ color: 'var(--color-danger)', fontSize: '11px' }}>{errors.email}</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label htmlFor="phone_1">Primary Phone</label>
                <input
                  id="phone_1"
                  name="phone_1"
                  value={formData.phone_1}
                  onChange={handleChange}
                  placeholder="e.g. 555-0199"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone_2">Secondary Phone (optional)</label>
                <input
                  id="phone_2"
                  name="phone_2"
                  value={formData.phone_2}
                  onChange={handleChange}
                  placeholder="e.g. 555-0188"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address_Line_1">Address Line 1</label>
              <input
                id="address_Line_1"
                name="address_Line_1"
                value={formData.address_Line_1}
                onChange={handleChange}
                placeholder="Street address, P.O. box"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label htmlFor="address_Line_2">Address Line 2 (optional)</label>
                <input
                  id="address_Line_2"
                  name="address_Line_2"
                  value={formData.address_Line_2}
                  onChange={handleChange}
                  placeholder="Apartment, suite, unit"
                />
              </div>

              <div className="form-group">
                <label htmlFor="address_Line_3">Address Line 3 (optional)</label>
                <input
                  id="address_Line_3"
                  name="address_Line_3"
                  value={formData.address_Line_3}
                  onChange={handleChange}
                  placeholder="Additional details"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input id="city" name="city" value={formData.city} onChange={handleChange} placeholder="e.g. Dallas" />
              </div>

              <div className="form-group">
                <label htmlFor="district">District</label>
                <input id="district" name="district" value={formData.district} onChange={handleChange} placeholder="e.g. Texas" />
              </div>

              <div className="form-group">
                <label htmlFor="country">Country</label>
                <input id="country" name="country" value={formData.country} onChange={handleChange} placeholder="e.g. USA" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <select id="status" name="status" value={formData.status} onChange={handleChange}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="role">Role *</label>
                <select id="role" name="role" value={formData.role} onChange={handleChange}>
                  <option value="Admin">Admin</option>
                  <option value="Project Manager">Project Manager</option>
                  <option value="Team Member">Team Member</option>
                  <option value="Client">Client</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              style={{ width: '100%', marginTop: '16px', padding: '12px' }}
            >
              {submitting ? 'Creating Account...' : 'Register'}
            </button>
          </form>

          <div className="form-links" style={{ marginTop: '20px', textAlign: 'center' }}>
            <span>Already have an account? </span>
            <Link to="/login">Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
